import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowUpRight, Volume2, VolumeX } from "lucide-react";
import { api, fmtEUR, fmtXOF } from "../lib/api";
import { MaskedLine, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { ProductCard } from "../components/ProductCard";

const IMG = "https://static.prod-images.emergentagent.com/jobs/152026f0-1513-45ae-b838-38d02f56f416/images";
const ATELIER = `${IMG}/ebcdf2e0e87505f5d26eb1d7cd400ced0705a69f15b22d05a6d88245f869bc4b.jpeg`;
const FITTING = `${IMG}/169f764d30d9e7b932aee909c3104469eb5bb7b22e683710859eb2f1b0fb88fd.jpeg`;

const HeroParallaxImage = () => {
  const { t } = useTranslation();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotateX = useTransform(sy, [-0.5, 0.5], [5, -5]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-7, 7]);
  const videoRef = useRef(null);
  const initRef = useRef(false);
  const [muted, setMuted] = useState(true);

  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;
    const next = !muted;
    el.muted = next;
    setMuted(next);
    if (!next) el.play().catch(() => {});
  };

  return (
    <motion.div
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
      }}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      style={{ perspective: 1200 }}
      className="relative hidden lg:block"
      data-testid="hero-visual"
    >
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className="relative w-[32vw] max-w-[470px] overflow-hidden"
      >
        <video
          data-testid="hero-video"
          ref={(el) => {
            videoRef.current = el;
            if (el && !initRef.current) {
              initRef.current = true;
              el.muted = true;
              el.play().catch(() => {});
            }
          }}
          src="/capsule-bkoss.mp4"
          autoPlay
          loop
          playsInline
          className="aspect-[3/4] w-full object-cover"
        />
        <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_90px_55px_#0A0908]" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-noir/40 via-transparent to-transparent" />
        <button
          data-testid="hero-video-sound-button"
          onClick={toggleSound}
          aria-label={muted ? t("home.soundOn") : t("home.soundOff")}
          title={muted ? t("home.soundOn") : t("home.soundOff")}
          className="absolute top-4 end-4 flex h-10 w-10 items-center justify-center border border-ivoire/40 bg-noir/60 text-ivoire backdrop-blur-sm transition-all duration-300 hover:border-ivoire hover:bg-noir"
        >
          {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
        </button>
      </motion.div>
      <motion.div
        style={{ transform: "translateZ(60px)" }}
        className="absolute bottom-12 start-[-4rem] border border-ligne px-6 py-5"
      >
        <p className="font-serif text-xl italic text-ivoire">{t("home.videoCard")}</p>
      </motion.div>
    </motion.div>
  );
};

const Hero = () => {
  const { t } = useTranslation();
  return (
    <section className="relative flex min-h-screen items-center overflow-hidden bg-noir noise">
      <div className="pointer-events-none absolute -right-40 top-1/2 h-[60vh] w-[60vh] -translate-y-1/2 rounded-full bg-or/10 blur-[140px]" />
      <div className="mx-auto grid w-full max-w-[1500px] grid-cols-1 items-center gap-16 px-6 pb-24 pt-40 lg:grid-cols-12 lg:px-12 lg:pt-32">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="eyebrow"
          >
            {t("home.heroEyebrow")}
          </motion.p>
          <h1 className="mt-8 font-serif uppercase leading-[0.95] tracking-tight text-ivoire">
            <MaskedLine delay={0.35} className="text-[13vw] sm:text-6xl lg:text-[5.2rem] xl:text-8xl">
              {t("home.t1")}
            </MaskedLine>
            <MaskedLine delay={0.5} className="text-[13vw] sm:text-6xl lg:text-[5.2rem] xl:text-8xl">
              {t("home.t2pre")} <span className="italic normal-case text-or">{t("home.t2it")}</span>
            </MaskedLine>
            <MaskedLine delay={0.65} className="text-[13vw] sm:text-6xl lg:text-[5.2rem] xl:text-8xl">
              {t("home.t3")}
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-8 max-w-md text-base leading-relaxed text-argile"
          >
            {t("home.heroText")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              data-testid="hero-cta-collection"
              to="/boutique"
              className="group flex items-center gap-3 bg-or px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-all duration-300 hover:bg-orclair"
            >
              {t("home.ctaCollection")}
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
            </Link>
            <Link
              data-testid="hero-cta-surmesure"
              to="/la-maison#rendez-vous"
              className="border border-ligne px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-ivoire transition-all duration-300 hover:border-or hover:text-or"
            >
              {t("home.ctaSurmesure")}
            </Link>
          </motion.div>
        </div>
        <div className="lg:col-span-5 lg:flex lg:justify-end">
          <HeroParallaxImage />
        </div>
      </div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 flex -translate-x-1/2 flex-col items-center gap-3"
      >
        <span className="font-mono text-[9px] uppercase tracking-[0.35em] text-argile">{t("home.scroll")}</span>
        <span className="block h-10 w-px animate-scroll-cue bg-or" />
      </motion.div>
    </section>
  );
};

const BentoTile = ({ to, testid, img, label, sub, delay = 0, pos = "object-center" }) => (
  <Reveal delay={delay} className="">
    <Link
      data-testid={testid}
      to={to}
      className="gold-frame frame-inner group relative block h-72 overflow-hidden border border-ligne lg:h-full"
    >
      <img src={img} alt={label} loading="lazy" className={`absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05] ${pos}`} />
      <div className="absolute inset-0 bg-gradient-to-t from-noir/90 via-noir/25 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-7">
        <div>
          <p className="font-serif text-3xl text-ivoire">{label}</p>
          <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.3em] text-or">{sub}</p>
        </div>
        <span className="flex h-11 w-11 items-center justify-center border border-ivoire/30 text-ivoire transition-all duration-300 group-hover:border-or group-hover:bg-or group-hover:text-noir">
          <ArrowUpRight size={17} />
        </span>
      </div>
    </Link>
  </Reveal>
);

const FALLBACK_CATS = [
  { key: "homme", labelKey: "nav.man", subKey: "bentoManSub", image: "/images/homme-polo.webp", pos: "object-[center_15%]" },
  { key: "femme", labelKey: "nav.woman", subKey: "bentoWomanSub", image: "/images/femme-tunique.jpg", pos: "object-top" },
  { key: "couple", labelKey: "nav.couple", subKey: "bentoCoupleSub", image: "/images/couple-reel.jpg", pos: "object-top" },
  { key: "enfants", labelKey: "nav.kids", subKey: "bentoKidsSub", image: "/images/enfants-reel.jpg", pos: "object-top" },
  { key: "maroquinerie", labelKey: "footer.catLeather", subKey: "bentoLeatherSub", image: "/images/maroquinerie-reel.jpg", pos: "object-center" },
];

const Collections = ({ counts }) => {
  const { t, i18n } = useTranslation();
  const [cats, setCats] = useState(FALLBACK_CATS);

  useEffect(() => {
    api.get("/categories").then((r) => { if (r.data?.length) setCats(r.data); }).catch(() => {});
  }, [i18n.language]);

  const catLabel = (c) => (c.labelKey && i18n.exists(c.labelKey) ? t(c.labelKey) : c.label);
  const catSub = (c) => {
    const n = counts[c.key] || 0;
    if (c.subKey && i18n.exists(`home.${c.subKey}`)) return t(`home.${c.subKey}`, { n });
    return t(n > 1 ? "common.piecesN" : "common.pieces1", { n });
  };

  return (
    <section className="mx-auto max-w-[1500px] px-6 py-24 lg:px-12 lg:py-32">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <Reveal>
          <p className="eyebrow">{t("home.colEyebrow")}</p>
          <h2 className="mt-4 font-serif text-3xl uppercase tracking-tight text-ivoire sm:text-4xl lg:text-5xl">
            {t("home.colPre")} <span className="italic normal-case text-or">{t("home.colIt")}</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link data-testid="collections-view-all" to="/boutique" className="group flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-argile transition-colors hover:text-or">
            {t("home.viewAll")} <ArrowRight size={13} className="transition-transform group-hover:translate-x-1 rtl:rotate-180" />
          </Link>
        </Reveal>
      </div>
      <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:auto-rows-[340px]">
        {cats.map((c, i) => (
          <BentoTile key={c.key} to={`/boutique?categorie=${c.key}`} testid={`bento-${c.key}`} img={c.image} label={catLabel(c)} sub={catSub(c)} pos={c.pos || "object-top"} delay={(i % 3) * 0.1} />
        ))}
        <Reveal delay={0.2}>
          <Link
            data-testid="bento-surmesure"
            to="/la-maison#rendez-vous"
            className="gold-frame group relative flex h-full min-h-[220px] flex-col justify-between overflow-hidden border border-ligne bg-card2 p-8 transition-colors"
          >
            <img src="/images/atelier-surmesure.png" alt="L'atelier sur-mesure B.KOSS" loading="lazy" className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-[1.05]" />
            <div className="absolute inset-0 bg-gradient-to-t from-noir/95 via-noir/60 to-noir/25" />
            <p className="eyebrow relative">{t("home.atelierEyebrow")}</p>
            <div className="relative">
              <p className="font-serif text-3xl text-ivoire">{t("home.atelierTitle")}</p>
              <p className="mt-3 max-w-xs text-sm leading-relaxed text-ivoire/80">
                {t("home.atelierText")}
              </p>
              <span className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-or">
                {t("home.bookRdv")} <ArrowRight size={13} className="transition-transform group-hover:translate-x-1 rtl:rotate-180" />
              </span>
            </div>
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const Spotlight = ({ product }) => {
  const { t } = useTranslation();
  if (!product) return null;
  return (
    <section className="relative overflow-hidden border-y border-ligne bg-card2 noise">
      <span className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 select-none whitespace-nowrap font-serif text-[22vw] uppercase italic text-ivoire/[0.035]">
        Éburnie
      </span>
      <div className="mx-auto grid max-w-[1500px] items-center gap-14 px-6 py-24 lg:grid-cols-2 lg:px-12 lg:py-32">
        <Reveal>
          <div className="gold-frame frame-inner relative overflow-hidden border border-ligne">
            <img src={product.image} alt={product.name} loading="lazy" className="aspect-[3/4] w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-noir/60 via-transparent to-transparent" />
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="eyebrow">{t("home.spotEyebrow")}</p>
          <h2 className="mt-5 font-serif text-4xl uppercase tracking-tight text-ivoire sm:text-5xl">{product.name}</h2>
          <p className="mt-6 max-w-lg leading-relaxed text-argile">{product.description}</p>
          <div className="mt-8 flex items-baseline gap-4" data-testid="spotlight-price">
            <p className="font-serif text-3xl text-or">{fmtXOF(product.price_xof)}</p>
            <p className="text-sm text-argile">{fmtEUR(product.price_xof)}</p>
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              data-testid="spotlight-cta"
              to={`/produit/${product.slug}`}
              className="group flex items-center gap-3 bg-or px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-all hover:bg-orclair"
            >
              {t("home.viewPiece")} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1 rtl:rotate-180" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Maison = () => {
  const { t } = useTranslation();
  const cities = [
    ["Abidjan", t("home.c1Sub")],
    ["Milan", t("home.c2Sub")],
    ["New York", t("home.c3Sub")],
  ];
  return (
    <section className="mx-auto grid max-w-[1500px] items-center gap-14 px-6 py-24 lg:grid-cols-2 lg:px-12 lg:py-32">
      <Reveal>
        <div className="gold-frame frame-inner relative overflow-hidden border border-ligne">
          <img src={ATELIER} alt="L'atelier B.KOSS à Abidjan" loading="lazy" className="aspect-[4/5] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-noir/60 to-transparent" />
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="eyebrow">{t("home.maisonEyebrow")}</p>
        <h2 className="mt-5 font-serif text-3xl uppercase tracking-tight text-ivoire sm:text-4xl lg:text-5xl">
          {t("home.maisonPre")} <span className="italic normal-case text-or">{t("home.maisonIt")}</span>
        </h2>
        <p className="mt-8 max-w-lg leading-relaxed text-argile">
          {t("home.maisonText")}
        </p>
        <div className="mt-10 grid grid-cols-3 gap-6 border-t border-ligne pt-8">
          {cities.map(([city, sub]) => (
            <div key={city}>
              <p className="font-serif text-xl text-ivoire">{city}</p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em] text-argile">{sub}</p>
            </div>
          ))}
        </div>
        <Link
          data-testid="maison-cta"
          to="/la-maison"
          className="mt-10 inline-flex items-center gap-3 border border-ligne px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-ivoire transition-all hover:border-or hover:text-or"
        >
          {t("home.maisonCta")} <ArrowRight size={14} className="rtl:rotate-180" />
        </Link>
      </Reveal>
    </section>
  );
};

const SurMesureBand = () => {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden border-y border-ligne">
      <img src={FITTING} alt="Séance de mesure au salon B.KOSS" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-noir/80" />
      <div className="relative mx-auto flex max-w-[1500px] flex-col items-start gap-8 px-6 py-24 lg:flex-row lg:items-center lg:justify-between lg:px-12 lg:py-32">
        <Reveal>
          <p className="eyebrow">{t("home.bandEyebrow")}</p>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl uppercase tracking-tight text-ivoire sm:text-4xl lg:text-5xl">
            {t("home.bandPre")} <span className="italic normal-case text-or">{t("home.bandIt")}</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            data-testid="surmesure-band-cta"
            to="/la-maison#rendez-vous"
            className="group flex items-center gap-3 bg-or px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-noir transition-all hover:bg-orclair"
          >
            {t("home.bandCta")} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1 rtl:rotate-180" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const TryonBand = () => {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden border-y border-ligne bg-card2 noise">
      <span className="pointer-events-none absolute end-0 top-1/2 -translate-y-1/2 select-none font-serif text-[18vw] uppercase italic text-ivoire/[0.03]">360°</span>
      <div className="relative mx-auto flex max-w-[1500px] flex-col items-start gap-8 px-6 py-24 lg:flex-row lg:items-center lg:justify-between lg:px-12 lg:py-28">
        <Reveal>
          <p className="eyebrow">{t("tryon.eyebrow")}</p>
          <h2 className="mt-4 max-w-2xl font-serif text-3xl uppercase tracking-tight text-ivoire sm:text-4xl lg:text-5xl">
            {t("tryon.titlePre")} <span className="italic normal-case text-or">{t("tryon.titleIt")}</span>
          </h2>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-argile">{t("tryon.intro")}</p>
        </Reveal>
        <Reveal delay={0.15}>
          <Link
            data-testid="tryon-band-cta"
            to="/essayage"
            className="group flex items-center gap-3 border border-or px-8 py-4 font-mono text-[11px] uppercase tracking-[0.3em] text-or transition-all duration-300 hover:bg-or hover:text-noir"
          >
            {t("tryon.generate")} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1 rtl:rotate-180" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  const { t, i18n } = useTranslation();
  const [nouveautes, setNouveautes] = useState([]);
  const [counts, setCounts] = useState({ homme: 0, femme: 0, couple: 0, enfants: 0, maroquinerie: 0 });
  const [spotlight, setSpotlight] = useState(null);

  useEffect(() => {
    api.get("/products").then((r) => setNouveautes(r.data.slice(0, 4))).catch(() => {});
    api.get("/collections").then((r) => setCounts(r.data)).catch(() => {});
    api.get("/products/ensemble-tunique-eburnie").then((r) => setSpotlight(r.data)).catch(() => {});
  }, [i18n.language]);

  return (
    <main data-testid="home-page">
      <Hero />
      <Marquee />
      <Collections counts={counts} />
      <Spotlight product={spotlight} />
      <section className="mx-auto max-w-[1500px] px-6 py-24 lg:px-12 lg:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <Reveal>
            <p className="eyebrow">{t("home.newsEyebrow")}</p>
            <h2 className="mt-4 font-serif text-3xl uppercase tracking-tight text-ivoire sm:text-4xl lg:text-5xl">
              {t("home.newsPre")} <span className="italic normal-case text-or">{t("home.newsIt")}</span>
            </h2>
          </Reveal>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {nouveautes.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>
      <SurMesureBand />
      <TryonBand />
      <Maison />
    </main>
  );
}
